import { MeshoptSimplifier } from 'three/examples/jsm/libs/meshopt_simplifier.module.js'
import { weldPrimitiveVertices } from './h3dVertexWeld.js'

const DEFAULT_MIN_TRIANGLES_PER_MESH = 12
const DEFAULT_POSITION_EPSILON_SCALE = 1e-5
const DEFAULT_NORMAL_MIN_DOT = 0.9999
// Generous ceiling so the requested triangle count (not simplification error) drives
// the reduction; meshopt still refuses collapses that would visibly wreck the mesh.
const TARGET_ERROR = 1

export async function optimizeModel(model, options = {}) {
  const opts = normalizeOptions(options)
  if (!opts.enabled) return { model, report: null }

  await MeshoptSimplifier.ready

  const newPositions = []
  const newNormals = []
  const newIndices = []
  const newPrimitives = []
  const meshes = []
  const warnings = []
  let sourceTriangles = 0
  let optimizedTriangles = 0
  let sourceVertices = 0
  let weldedVertices = 0
  let compactedVertices = 0
  let degenerateTrianglesRemoved = 0

  model.primitives.forEach((primitive, primitiveIndex) => {
    const local = extractPrimitiveVertexData(model, primitive)
    const sourceTriangleCount = primitive.indexCount / 3
    sourceTriangles += sourceTriangleCount

    const positionEpsilon = opts.weld.positionEpsilon ?? opts.weld.positionEpsilonScale * boundingDiagonal(local.positions)
    const welded = weldPrimitiveVertices(local, { positionEpsilon, normalMinDot: opts.weld.normalMinDot })
    const weldedTriangleCount = welded.indices.length / 3
    if (weldedTriangleCount + welded.degenerateTrianglesRemoved !== sourceTriangleCount) {
      throw new Error(
        `H3D0 optimizer weld accounting mismatch on primitive${primitiveIndex}: `
        + `${sourceTriangleCount} source triangles vs ${weldedTriangleCount} welded + ${welded.degenerateTrianglesRemoved} degenerate`,
      )
    }
    sourceVertices += welded.sourceVertices
    weldedVertices += welded.weldedVertices
    degenerateTrianglesRemoved += welded.degenerateTrianglesRemoved
    if (welded.degenerateTrianglesRemoved > 0) {
      warnings.push(`primitive${primitiveIndex}: welding produced ${welded.degenerateTrianglesRemoved} degenerate triangle(s); removed`)
    }

    const targetTriangleCount = Math.max(opts.minTrianglesPerMesh, Math.round(sourceTriangleCount * opts.targetRatio))
    const targetIndexCount = Math.min(welded.indices.length, Math.floor(targetTriangleCount) * 3)

    let resultIndices = welded.indices
    if (targetIndexCount < welded.indices.length) {
      // meshopt_simplify only ever collapses existing vertices onto each other - it never
      // synthesizes a blended vertex - so the surviving indices still point at the welded
      // position/normal buffer and need no attribute recomputation.
      const [simplified] = MeshoptSimplifier.simplify(
        Uint32Array.from(welded.indices),
        welded.positions,
        3,
        targetIndexCount,
        TARGET_ERROR,
        ['LockBorder'],
      )
      if (simplified.length <= resultIndices.length) {
        resultIndices = simplified
      } else {
        warnings.push(`primitive${primitiveIndex}: simplifier returned more triangles than the source; keeping welded geometry`)
      }
    }

    // Simplification (and, rarely, degenerate-triangle removal during welding) can leave
    // vertices in the welded buffer that the final index buffer no longer references -
    // compact those away so only what's actually drawn ships in the H3D0 file.
    const compacted = compactReferencedVertices(resultIndices, welded.positions, welded.normals, welded.uvs)

    const optimizedTriangleCount = compacted.indices.length / 3
    const vertexBase = newPositions.length / 3
    const firstIndex = newIndices.length
    for (let i = 0; i < compacted.indices.length; i += 1) newIndices.push(compacted.indices[i] + vertexBase)
    for (let i = 0; i < compacted.positions.length; i += 1) newPositions.push(compacted.positions[i])
    for (let i = 0; i < compacted.normals.length; i += 1) newNormals.push(compacted.normals[i])
    newPrimitives.push({ firstIndex, indexCount: compacted.indices.length, materialIndex: primitive.materialIndex })

    optimizedTriangles += optimizedTriangleCount
    compactedVertices += compacted.compactedVertices
    meshes.push({
      name: `primitive${primitiveIndex}`,
      sourceVertices: welded.sourceVertices,
      weldedVertices: welded.weldedVertices,
      compactedVertices: compacted.compactedVertices,
      vertexReduction: welded.sourceVertices > 0 ? 1 - welded.weldedVertices / welded.sourceVertices : 0,
      sourceTriangles: sourceTriangleCount,
      optimizedTriangles: optimizedTriangleCount,
      ratio: sourceTriangleCount > 0 ? optimizedTriangleCount / sourceTriangleCount : 1,
      degenerateTrianglesRemoved: welded.degenerateTrianglesRemoved,
    })
  })

  if (optimizedTriangles !== newIndices.length / 3) {
    throw new Error(
      `H3D0 optimizer accounting mismatch: ${optimizedTriangles} optimized triangles vs ${newIndices.length / 3} indices written`,
    )
  }

  return {
    model: {
      positions: new Float32Array(newPositions),
      normals: new Float32Array(newNormals),
      indices: new Uint32Array(newIndices),
      primitives: newPrimitives,
      materials: model.materials,
      // Lights are scene data, untouched by geometry optimization.
      lights: model.lights,
    },
    report: {
      sourceTriangles,
      optimizedTriangles,
      reductionRatio: sourceTriangles > 0 ? optimizedTriangles / sourceTriangles : 1,
      sourceVertices,
      weldedVertices,
      compactedVertices,
      vertexReduction: sourceVertices > 0 ? 1 - weldedVertices / sourceVertices : 0,
      degenerateTrianglesRemoved,
      meshes,
      warnings,
    },
  }
}

// Pulls out just the vertices a primitive's index range actually references, as a dense
// 0-based local buffer - required both to keep welding scoped to one primitive and to
// hand meshopt a compact buffer once welding has collapsed duplicates.
function extractPrimitiveVertexData(model, primitive) {
  const globalIndices = model.indices.subarray(primitive.firstIndex, primitive.firstIndex + primitive.indexCount)
  const globalToLocal = new Map()
  const localVertexIds = []
  const localIndices = new Uint32Array(globalIndices.length)
  for (let i = 0; i < globalIndices.length; i += 1) {
    const g = globalIndices[i]
    let local = globalToLocal.get(g)
    if (local === undefined) {
      local = localVertexIds.length
      localVertexIds.push(g)
      globalToLocal.set(g, local)
    }
    localIndices[i] = local
  }

  const vertexCount = localVertexIds.length
  const localPositions = new Float32Array(vertexCount * 3)
  const localNormals = new Float32Array(vertexCount * 3)
  const localUvs = model.uvs ? new Float32Array(vertexCount * 2) : null
  localVertexIds.forEach((g, local) => {
    localPositions[local * 3] = model.positions[g * 3]
    localPositions[local * 3 + 1] = model.positions[g * 3 + 1]
    localPositions[local * 3 + 2] = model.positions[g * 3 + 2]
    localNormals[local * 3] = model.normals[g * 3]
    localNormals[local * 3 + 1] = model.normals[g * 3 + 1]
    localNormals[local * 3 + 2] = model.normals[g * 3 + 2]
    if (localUvs) {
      localUvs[local * 2] = model.uvs[g * 2]
      localUvs[local * 2 + 1] = model.uvs[g * 2 + 1]
    }
  })

  return { positions: localPositions, normals: localNormals, uvs: localUvs, indices: localIndices }
}

// Builds a deterministic old-index -> compact-index remap in order of first occurrence
// in the (already simplified) index buffer, then copies only the referenced vertices.
// Triangle order/winding and material assignment are untouched - only vertex identity
// and numbering change.
function compactReferencedVertices(indices, positions, normals, uvs) {
  const remap = new Map()
  const order = []
  const compactIndices = new Uint32Array(indices.length)
  for (let i = 0; i < indices.length; i += 1) {
    const oldIndex = indices[i]
    let compact = remap.get(oldIndex)
    if (compact === undefined) {
      compact = order.length
      order.push(oldIndex)
      remap.set(oldIndex, compact)
    }
    compactIndices[i] = compact
  }

  const compactedVertices = order.length
  const compactPositions = new Float32Array(compactedVertices * 3)
  const compactNormals = new Float32Array(compactedVertices * 3)
  const compactUvs = uvs ? new Float32Array(compactedVertices * 2) : null
  order.forEach((oldIndex, compact) => {
    compactPositions[compact * 3] = positions[oldIndex * 3]
    compactPositions[compact * 3 + 1] = positions[oldIndex * 3 + 1]
    compactPositions[compact * 3 + 2] = positions[oldIndex * 3 + 2]
    compactNormals[compact * 3] = normals[oldIndex * 3]
    compactNormals[compact * 3 + 1] = normals[oldIndex * 3 + 1]
    compactNormals[compact * 3 + 2] = normals[oldIndex * 3 + 2]
    if (compactUvs) {
      compactUvs[compact * 2] = uvs[oldIndex * 2]
      compactUvs[compact * 2 + 1] = uvs[oldIndex * 2 + 1]
    }
  })

  return { indices: compactIndices, positions: compactPositions, normals: compactNormals, uvs: compactUvs, compactedVertices }
}

function boundingDiagonal(positions) {
  let minX = Infinity; let minY = Infinity; let minZ = Infinity
  let maxX = -Infinity; let maxY = -Infinity; let maxZ = -Infinity
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i]; const y = positions[i + 1]; const z = positions[i + 2]
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
    if (z < minZ) minZ = z
    if (z > maxZ) maxZ = z
  }
  return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) || 1
}

function normalizeOptions(options) {
  return {
    enabled: Boolean(options?.enabled),
    targetRatio: options?.targetRatio ?? 1,
    minTrianglesPerMesh: options?.minTrianglesPerMesh ?? DEFAULT_MIN_TRIANGLES_PER_MESH,
    weld: {
      positionEpsilon: options?.weld?.positionEpsilon,
      positionEpsilonScale: options?.weld?.positionEpsilonScale ?? DEFAULT_POSITION_EPSILON_SCALE,
      normalMinDot: options?.weld?.normalMinDot ?? DEFAULT_NORMAL_MIN_DOT,
    },
  }
}
